import { createRouter, createWebHistory, type NavigationGuardNext, type RouteLocationAsRelativeGeneric, type RouteLocationNormalizedGeneric, type RouteLocationNormalizedLoadedGeneric, type Router } from 'vue-router'
//import { useSpinnerStore } from '@/stores'
import { inject, nextTick } from 'vue'
import { AUTH_STATE, MEMBER_API, type IApplicationApi } from '@/api'
import log from 'loglevel'
import z from 'zod'
import { unique } from 'remeda'
import { DateOption } from '@/components'
import type { CommonPageProps } from '@/pages'
import { Districts } from '@/models'

async function LoadCommonData(api: IApplicationApi): Promise<CommonPageProps> {
  const member = await api.getProfile()
  const unreadCount = await api.getUnreadCount()
  const districts = await api.getDistricts({
    ids: unique(member.students.map(s => s.districtId))
  })

  return { member, unreadCount, districts, bannerLogo: Districts.GetBannerLogo(districts) }
}

async function LoadCommonDataAndSetAsProps(api: IApplicationApi, to: RouteLocationNormalizedGeneric) {
  to.meta.props = await LoadCommonData(api)
}

interface RouteInfo {
  readonly Name: string,
  readonly Path: string
}

class Login implements RouteInfo {
  public readonly Name = 'Login'
  public readonly Path = '/login'

  GetHref(): RouteLocationAsRelativeGeneric {
    return { name: this.Name }
  }

  Navigate(router: Router) {
    router.push(this.GetHref())
  }
}

class LoginWithGoogle implements RouteInfo {
  public readonly Name = 'Login With Google'
  public readonly Path = '/login/google'

  GetHref(): RouteLocationAsRelativeGeneric {
    return { name: this.Name }
  }

  Navigate(router: Router) {
    router.push(this.GetHref())
  }
}

class LoginWithMicrosoft implements RouteInfo {
  public readonly Name = 'Login With Microsoft'
  public readonly Path = '/login/microsoft'

  GetHref(): RouteLocationAsRelativeGeneric {
    return { name: this.Name }
  }

  Navigate(router: Router) {
    router.push(this.GetHref())
  }
}

class ForgotPassword implements RouteInfo {
  public readonly Name = 'Forgot Password'
  public readonly Path = '/forgot-password'

  GetHref(): RouteLocationAsRelativeGeneric {
    return { name: this.Name }
  }

  Navigate(router: Router) {
    router.push(this.GetHref())
  }

  GetParameters(route: RouteLocationNormalizedLoadedGeneric) {
    const result = z.object({
      token: z.string().nonempty(),
      email: z.string().nonempty()
    }).safeParse(route.query)

    if (result.success) {
      return result.data
    }
  }
}

class Register implements RouteInfo {
  public readonly Name = 'Register'
  public readonly Path = '/register'

  GetHref(token: string, email: string): RouteLocationAsRelativeGeneric {
    return { name: this.Name, query: { token, email } }
  }

  Navigate(router: Router, token: string, email: string) {
    router.push(this.GetHref(token, email))
  }

  GetParameters(route: RouteLocationNormalizedLoadedGeneric) {
    const result = z.object({
      token: z.string().nonempty(),
      email: z.string().nonempty()
    }).safeParse(route.query)

    if (result.success) {
      return result.data
    }
  }
}

class Logout implements RouteInfo {
  public readonly Name = 'Logout'
  public readonly Path = '/log-out'

  GetHref(): RouteLocationAsRelativeGeneric {
    return { name: this.Name }
  }

  Navigate(router: Router) {
    router.push(this.GetHref())
  }
}

class HomeRoom implements RouteInfo {
  public readonly Name = 'My Feed'
  public readonly Path = '/'

  GetHref(): RouteLocationAsRelativeGeneric {
    return { name: this.Name }
  }

  Navigate(router: Router) {
    router.push(this.GetHref())
  }

  GetParameters(route: RouteLocationNormalizedLoadedGeneric) {
    const result = z.object({
      range: z.enum([ DateOption.Last2Years, DateOption.Last3Months, DateOption.Last6Months, DateOption.LastYear ]).optional(),
      search: z.string().optional(),
    }).safeParse(route.query)

    if (result.success) {
      return result.data
    }
  }
}

class Digest implements RouteInfo {
  public readonly Name = 'Digest'
  public readonly Path = '/d/:date(\\d{6})'

  GetHref(date: string): RouteLocationAsRelativeGeneric {
    return { name: this.Name, params: { date } }
  }

  Navigate(router: Router, date: string) {
    router.push(this.GetHref(date))
  }

  ParseDate(yymmdd: string): Date | undefined {
    const yy = parseInt(yymmdd.substring(0, 2), 10)
    const mm = parseInt(yymmdd.substring(2, 4), 10)
    const dd = parseInt(yymmdd.substring(4, 6), 10)
    if (isNaN(yy) || isNaN(mm) || isNaN(dd)) return undefined
    const year = 2000 + yy
    const date = new Date(year, mm - 1, dd)
    if (date.getFullYear() === year && date.getMonth() === mm - 1 && date.getDate() === dd) {
      return date
    }
    return undefined
  }

  GetParameters(route: RouteLocationNormalizedLoadedGeneric) {
    const result = z.object({
      date: z.string().refine(s => Routes.Digest.ParseDate(s) !== undefined)
    }).safeParse(route.params)

    if (result.success) {
      return result.data
    }
  }

  async BeforeRouteEnter(api: IApplicationApi, to: RouteLocationNormalizedGeneric, _: RouteLocationNormalizedLoadedGeneric, next: NavigationGuardNext) {
    const params = Routes.Digest.GetParameters(to as unknown as RouteLocationNormalizedLoadedGeneric)

    if (!params) {
      next(Routes.NotFound.GetHref())
      return
    }

    await LoadCommonDataAndSetAsProps(api, to)
    next()
  }
}

class Announcement implements RouteInfo {
  public readonly Name = 'Announcement'
  public readonly Path = '/announcement/:announcementId'

  GetHref(announcementId: number, translation: string): RouteLocationAsRelativeGeneric {
    return { name: this.Name, params: { announcementId }, query: { translation } }
  }

  Navigate(router: Router, announcementId: number, translation: string) {
    router.push(this.GetHref(announcementId, translation))
  }

  GetParameters(route: RouteLocationNormalizedLoadedGeneric) {
    const result = z.object({
      announcementId: z.coerce.number(),
      translation: z.coerce.string()
    }).safeParse({...route.params, ...route.query})

    if (result.success) {
      return result.data
    }
  }

  async BeforeRouteEnter(api: IApplicationApi, to: RouteLocationNormalizedGeneric, from: RouteLocationNormalizedLoadedGeneric, next: NavigationGuardNext) {
    const post = await this.LoadDataAndSetProps(api, to)

    if (!post) {
      this.HandleNavigationFailure(from, next)
    }
    else {
      next()
    }
  }

  async LoadDataAndSetProps(api: IApplicationApi, to: RouteLocationNormalizedGeneric) {
    const announcement = await this.LoadData(api, to)
    const common = await LoadCommonData(api)
    to.meta.props = { ...common, announcement }
    return announcement
  }

  async LoadData(api: IApplicationApi, to: RouteLocationNormalizedGeneric) {
    const params = Routes.Announcement.GetParameters(to)

    if (!params) return

    try {
      return await api.getAnnouncement({ announcementId: params.announcementId, translation: params.translation })
    }
    catch {
      return undefined
    }
  }

  HandleNavigationFailure(_: RouteLocationNormalizedLoadedGeneric, next: NavigationGuardNext) {
    next(false)
  }
}

class PostDetail implements RouteInfo {
  public readonly Name = 'Post Detail'
  public readonly Path = '/post/:postId'

  Navigate(router: Router, postId: number) {
    router.push(this.GetHref(postId))
  }

  GetHref(postId: number): RouteLocationAsRelativeGeneric {
    return { name: this.Name, params: { postId } }
  }

  GetParameters(route: RouteLocationNormalizedLoadedGeneric) {
    const result = z.object({
      postId: z.coerce.number()
    }).safeParse(route.params)

    if (result.success) {
      return result.data
    }
  }

  async BeforeRouteEnter(api: IApplicationApi, to: RouteLocationNormalizedGeneric, from: RouteLocationNormalizedLoadedGeneric, next: NavigationGuardNext) {
    const post = await this.LoadDataAndSetProps(api, to)

    if (!post) {
      this.HandleNavigationFailure(from, next)
    }
    else {
      next()
    }
  }

  async LoadDataAndSetProps(api: IApplicationApi, to: RouteLocationNormalizedGeneric) {
    const post = await this.LoadData(api, to)
    const common = await LoadCommonData(api)
    to.meta.props = { ...common, post }
    return post
  }

  async LoadData(api: IApplicationApi, to: RouteLocationNormalizedGeneric) {
    const params = Routes.PostDetail.GetParameters(to)

    if (!params) return

    try {
      return await api.getMessage({ messageId: params.postId })
    }
    catch {
      return undefined
    }
  }

  HandleNavigationFailure(_: RouteLocationNormalizedLoadedGeneric, next: NavigationGuardNext) {
    next(false)
  }
}

class Conversations implements RouteInfo {
  public readonly Name = 'Conversations'
  public readonly Path = '/conversations'

  Navigate(router: Router, conversation?: number) {
    router.push(this.GetHref(conversation))
  }

  GetHref(conversation?: number): RouteLocationAsRelativeGeneric {
    return { name: this.Name, query: { conversation } }
  }

  GetParameters(route: RouteLocationNormalizedLoadedGeneric) {
    return z.object({
      conversation: z.coerce.number().optional()
    }).parse(route.query)
  }

  async BeforeRouteEnter(api: IApplicationApi, to: RouteLocationNormalizedGeneric, from: RouteLocationNormalizedLoadedGeneric, next: NavigationGuardNext) {
    const props = await this.LoadDataAndSetProps(api, to)

    if (!props) {
      this.HandleNavigationFailure(from, next)
    }
    else {
      next()
    }
  }

  async LoadDataAndSetProps(api: IApplicationApi, to: RouteLocationNormalizedGeneric) {
    const props = await this.LoadData(api, to)
    to.meta.props = props
    return props
  }

  async LoadData(api: IApplicationApi, to: RouteLocationNormalizedGeneric) {
    const params = Routes.Conversations.GetParameters(to)

    if (!params) return

    try {
      return await LoadCommonData(api)
    }
    catch {
    }
  }

  HandleNavigationFailure(_: RouteLocationNormalizedLoadedGeneric, next: NavigationGuardNext) {
    next(false)
  }
}

class NewConversationRecipients implements RouteInfo {
  public readonly Name = 'Conversation - Select Recipients'
  public readonly Path = '/conversation/select-recipients'

  Navigate(router: Router, draftId?: number) {
    router.push(this.GetHref(draftId))
  }

  GetHref(draftId?: number): RouteLocationAsRelativeGeneric {
    return { name: this.Name, query: { draftId } }
  }

  GetParameters(route: RouteLocationNormalizedLoadedGeneric) {
    return z.object({
      draftId: z.coerce.number().optional()
    }).parse(route.query)
  }
}

class NewConversationWrite implements RouteInfo {
  public readonly Name = 'Conversation - Write'
  public readonly Path = '/conversation/write'

  Navigate(router: Router, draftId: number) {
    router.push(this.GetHref(draftId))
  }

  GetHref(draftId: number): RouteLocationAsRelativeGeneric {
    return { name: this.Name, query: { draftId } }
  }

  GetParameters(route: RouteLocationNormalizedLoadedGeneric) {
    return z.object({
      draftId: z.coerce.number()
    }).parse(route.query)
  }
}

class ConversationDetail implements RouteInfo {
  public readonly Name = 'Conversation Detail'
  public readonly Path = '/conversation/:conversationId/detail'

  Navigate(router: Router, conversationId: number) {
    router.push(this.GetHref(conversationId))
  }

  GetHref(conversationId: number): RouteLocationAsRelativeGeneric {
    return { name: this.Name, params: { conversationId } }
  }

  GetParameters(route: RouteLocationNormalizedLoadedGeneric) {
    const result = z.object({
      conversationId: z.coerce.number()
    }).safeParse(route.params)

    if (result.success) {
      return result.data
    }
  }

  async BeforeRouteEnter(api: IApplicationApi, to: RouteLocationNormalizedGeneric, from: RouteLocationNormalizedLoadedGeneric, next: NavigationGuardNext) {
    const conversation = await this.LoadDataAndSetProps(api, to)

    if (!conversation) {
      this.HandleNavigationFailure(from, next)
    }
    else {
      next()
    }
  }

  async LoadDataAndSetProps(api: IApplicationApi, to: RouteLocationNormalizedGeneric) {
    const conversation = await this.LoadData(api, to)
    const common = await LoadCommonData(api)
    to.meta.props = { ...common, conversation }
    return conversation
  }

  async LoadData(api: IApplicationApi, to: RouteLocationNormalizedGeneric) {
    const params = Routes.ConversationDetail.GetParameters(to)

    if (!params) return

    try {
      return await api.getThread({ threadId: params.conversationId })
    }
    catch {
      return undefined
    }
  }

  HandleNavigationFailure(_: RouteLocationNormalizedLoadedGeneric, next: NavigationGuardNext) {
    next(false)
  }
}

class Alerts implements RouteInfo {
  public readonly Name = 'Alerts'
  public readonly Path = '/alerts'

  GetHref(): RouteLocationAsRelativeGeneric {
    return { name: this.Name }
  }

  Navigate(router: Router) {
    router.push(this.GetHref())
  }
}

class Calendar implements RouteInfo {
  public readonly Name = 'Calendar'
  public readonly Path = '/calendar'

  GetHref(): RouteLocationAsRelativeGeneric {
    return { name: this.Name }
  }

  Navigate(router: Router) {
    router.push(this.GetHref())
  }
}

class CalendarEventDetail implements RouteInfo {
  public readonly Name = 'Calendar - Event Detail'
  public readonly Path = '/calendar/:calendarId/event/:eventId'

  Navigate(router: Router, params: { eventId: string; calendarId: string }) {
    router.push(this.GetHref(params))
  }

  GetHref(params: { eventId: string; calendarId: string }): RouteLocationAsRelativeGeneric {
    return { name: this.Name, params }
  }

  GetParameters(route: RouteLocationNormalizedLoadedGeneric) {
    return z.object({
      eventId: z.string(),
      calendarId: z.string()
    }).parse(route.params)
  }
}

class MyAccount implements RouteInfo {
  public readonly Name = 'My Account'
  public readonly Path = '/me'

  GetHref(): RouteLocationAsRelativeGeneric {
    return { name: this.Name }
  }

  Navigate(router: Router) {
    router.push(this.GetHref())
  }
}

class MyAccountEdit implements RouteInfo {
  public readonly Name = 'My Account - Edit'
  public readonly Path = '/me/edit'

  GetHref(): RouteLocationAsRelativeGeneric {
    return { name: this.Name }
  }

  Navigate(router: Router) {
    router.push(this.GetHref())
  }
}

class MyAccountNotifications implements RouteInfo {
  public readonly Name = 'My Account - Notifications'
  public readonly Path = '/me/notifications'

  GetHref(): RouteLocationAsRelativeGeneric {
    return { name: this.Name }
  }

  Navigate(router: Router) {
    router.push(this.GetHref())
  }
}

class MyAccountSecurity implements RouteInfo {
  public readonly Name = 'My Account - Security'
  public readonly Path = '/me/security'

  GetHref(): RouteLocationAsRelativeGeneric {
    return { name: this.Name }
  }

  Navigate(router: Router) {
    router.push(this.GetHref())
  }
}

class Files implements RouteInfo {
  public readonly Name = 'Files'
  public readonly Path = '/files'

  GetHref(): RouteLocationAsRelativeGeneric {
    return { name: this.Name }
  }

  Navigate(router: Router) {
    router.push(this.GetHref())
  }
}

class Groups implements RouteInfo {
  public readonly Name = 'Groups'
  public readonly Path = '/groups'

  GetHref(): RouteLocationAsRelativeGeneric {
    return { name: this.Name }
  }

  Navigate(router: Router) {
    router.push(this.GetHref())
  }
}

class StudentProfile implements RouteInfo {
  public readonly Name = 'Student Profile'
  public readonly Path = '/student/:studentId'

  Navigate(router: Router, studentId: number) {
    router.push({ name: this.Name, params: { studentId } })
  }

  GetHref(studentId: number): RouteLocationAsRelativeGeneric {
    return { name: this.Name, params: { studentId } }
  }

  GetParameters(route: RouteLocationNormalizedLoadedGeneric) {
    const result = z.object({
      studentId: z.coerce.number()
    }).safeParse(route.params)

    if (result.success) {
      return result.data
    }
  }

  async BeforeRouteEnter(api: IApplicationApi, to: RouteLocationNormalizedGeneric, from: RouteLocationNormalizedLoadedGeneric, next: NavigationGuardNext) {
    const props = await this.LoadDataAndSetProps(api, to)

    if (!props) {
      this.HandleNavigationFailure(from, next)
    }
    else {
      next()
    }
  }

  async LoadDataAndSetProps(api: IApplicationApi, to: RouteLocationNormalizedGeneric) {
    const props = await this.LoadData(api, to)
    to.meta.props = props
    return props
  }

  async LoadData(api: IApplicationApi, to: RouteLocationNormalizedGeneric) {
    const params = Routes.StudentProfile.GetParameters(to)

    if (!params) return

    try {
      const common = await LoadCommonData(api)
      const student = common.member.students.find(s => s.studentId === params.studentId)

      if (!student) return

      const district = common.districts.find(d => d.districtId === student?.districtId)
      const school = district?.schools.find(s => s.schoolId === student?.schoolId)
      const contacts = await api.getStudentContacts({ studentId: student?.studentId })

      return { ...common, student, school, contacts }
    }
    catch {
    }
  }

  HandleNavigationFailure(_: RouteLocationNormalizedLoadedGeneric, next: NavigationGuardNext) {
    next(false)
  }
}

class ResetPassword implements RouteInfo {
  public readonly Name = 'Reset Password'
  public readonly Path = '/reset-password'

  GetHref(): RouteLocationAsRelativeGeneric {
    return { name: this.Name }
  }

  Navigate(router: Router) {
    router.push(this.GetHref())
  }
}

class Forbidden implements RouteInfo {
  public readonly Name = 'Forbidden'
  public readonly Path = '/forbidden'

  GetHref(): RouteLocationAsRelativeGeneric {
    return { name: this.Name }
  }

  Navigate(router: Router) {
    router.push(this.GetHref())
  }
}

class NotFound implements RouteInfo {
  public readonly Name = 'Not Found'
  public readonly Path = '/not-found'

  GetHref(): RouteLocationAsRelativeGeneric {
    return { name: this.Name }
  }

  Navigate(router: Router) {
    router.push(this.GetHref())
  }
}

class Error implements RouteInfo {
  public readonly Name = 'Error'
  public readonly Path = '/error'

  GetHref(): RouteLocationAsRelativeGeneric {
    return { name: this.Name }
  }

  Navigate(router: Router) {
    router.push(this.GetHref())
  }
}

class PrivacyPolicy implements RouteInfo {
  public readonly Name = 'Privacy Policy'
  public readonly Path = '/privacy-policy'

  GetHref(): RouteLocationAsRelativeGeneric {
    return { name: this.Name }
  }

  Navigate(router: Router) {
    router.push(this.GetHref())
  }
}

export class Routes {
  public static readonly Login = new Login()
  public static readonly LoginWithGoogle = new LoginWithGoogle()
  public static readonly LoginWithMicrosoft = new LoginWithMicrosoft()
  public static readonly ForgotPassword = new ForgotPassword()
  public static readonly Register = new Register()
  public static readonly Logout = new Logout()
  public static readonly MyFeed = new HomeRoom()
  public static readonly Digest = new Digest()
  public static readonly PostDetail = new PostDetail()
  public static readonly Announcement = new Announcement()
  public static readonly Conversations = new Conversations()
  public static readonly NewConversationRecipients = new NewConversationRecipients()
  public static readonly NewConversationWrite = new NewConversationWrite()
  public static readonly ConversationDetail = new ConversationDetail()
  public static readonly Alerts = new Alerts()
  public static readonly Calendar = new Calendar()
  public static readonly CalendarEventDetail = new CalendarEventDetail()
  public static readonly MyAccount = new MyAccount()
  public static readonly MyAccountEdit = new MyAccountEdit()
  public static readonly MyAccountNotifications = new MyAccountNotifications()
  public static readonly MyAccountSecurity = new MyAccountSecurity()
  public static readonly Files = new Files()
  public static readonly Groups = new Groups()
  public static readonly StudentProfile = new StudentProfile()
  public static readonly ResetPassword = new ResetPassword()
  public static readonly Forbidden = new Forbidden()
  public static readonly NotFound = new NotFound()
  public static readonly Error = new Error()
  public static readonly PrivacyPolicy = new PrivacyPolicy()
}

declare module 'vue-router' {
  interface RouteMeta {
    authorize?: boolean
    props?: unknown
    filterKey?: string
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: Routes.Login.Path,
      name: Routes.Login.Name,
      component: () => import('@/pages/LoginPublic.vue'),
      meta: {}
    },
    {
      path: Routes.LoginWithMicrosoft.Path,
      name: Routes.LoginWithMicrosoft.Name,
      component: () => import('@/pages/LoginMicrosoft.vue'),
      meta: {}
    },
    {
      path: Routes.LoginWithGoogle.Path,
      name: Routes.LoginWithGoogle.Name,
      component: () => import('@/pages/LoginGoogle.vue'),
      meta: {}
    },
    {
      path: Routes.ForgotPassword.Path,
      name: Routes.ForgotPassword.Name,
      component: () => import('@/pages/ForgotPassword.vue'),
      meta: {}
    },
    {
      path: Routes.PrivacyPolicy.Path,
      name: Routes.PrivacyPolicy.Name,
      component: () => import('@/pages/PrivacyPolicy.vue'),
      meta: {}
    },
    {
      path: Routes.Logout.Path,
      name: Routes.Logout.Name,
      component: () => import('@/pages/LogOut.vue'),
      meta: {
        authorize: true
      }
    },
    {
      path: Routes.Register.Path,
      name: Routes.Register.Name,
      component: () => import('@/pages/Register.vue'),
      meta: {}
    },
    {
      path: Routes.MyFeed.Path,
      name: Routes.MyFeed.Name,
      beforeEnter: (to) => LoadCommonDataAndSetAsProps(inject(MEMBER_API)!, to),
      props: (to) => to.meta.props,
      component: () => import('@/pages/MyFeed.vue'),
      meta: {
        authorize: true,
        filterKey: Routes.MyFeed.Name
      }
    },
    {
      path: Routes.Digest.Path,
      name: Routes.Digest.Name,
      component: () => import('@/pages/Digest.vue'),
      beforeEnter: (to, from, next) => Routes.Digest.BeforeRouteEnter(inject(MEMBER_API)!, to, from, next),
      props: (to) => to.meta.props,
      meta: {
        authorize: true
      },
    },
    {
      path: Routes.PostDetail.Path,
      name: Routes.PostDetail.Name,
      component: () => import('@/pages/PostView.vue'),
      beforeEnter: (to, from, next) => Routes.PostDetail.BeforeRouteEnter(inject(MEMBER_API)!, to, from, next),
      props: (to) => to.meta.props,
      meta: {
        authorize: true
      },
    },
    {
      path: Routes.Announcement.Path,
      name: Routes.Announcement.Name,
      component: () => import('@/pages/AnnouncementView.vue'),
      beforeEnter: (to, from, next) => Routes.Announcement.BeforeRouteEnter(inject(MEMBER_API)!, to, from, next),
      props: (to) => to.meta.props,
      meta: {
        authorize: true
      },
    },
    {
      path: Routes.Conversations.Path,
      name: Routes.Conversations.Name,
      component: () => import('@/pages/Conversations.vue'),
      beforeEnter: (to, from, next) => Routes.Conversations.BeforeRouteEnter(inject(MEMBER_API)!, to,  from, next),
      props: (to) => to.meta.props,
      meta: {
        authorize: true,
        filterKey: Routes.Conversations.Name
      },
    },
    {
      path: Routes.ConversationDetail.Path,
      name: Routes.ConversationDetail.Name,
      component: () => import('@/pages/ConversationDetailMobile.vue'),
      beforeEnter: (to, from, next) => Routes.ConversationDetail.BeforeRouteEnter(inject(MEMBER_API)!, to, from, next),
      props: (to) => to.meta.props,
      meta: {
        authorize: true,
        filterKey: Routes.Conversations.Name
      },
    },
    {
      path: Routes.NewConversationRecipients.Path,
      name: Routes.NewConversationRecipients.Name,
      component: () => import('@/pages/ConversationNew.vue'),
      beforeEnter: (to) => LoadCommonDataAndSetAsProps(inject(MEMBER_API)!, to),
      props: (to) => to.meta.props,
      meta: {
        authorize: true
      },
    },
    {
      path: Routes.NewConversationWrite.Path,
      name: Routes.NewConversationWrite.Name,
      component: () => import('@/pages/ConversationWriteMobile.vue'),
      beforeEnter: (to) => LoadCommonDataAndSetAsProps(inject(MEMBER_API)!, to),
      props: (to) => to.meta.props,
      meta: {
        authorize: true,
      },
    },
    {
      path: Routes.Alerts.Path,
      name: Routes.Alerts.Name,
      component: () => import('@/pages/Alerts.vue'),
      beforeEnter: (to) => LoadCommonDataAndSetAsProps(inject(MEMBER_API)!, to),
      props: (to) => to.meta.props,
      meta: {
        authorize: true
      },
    },
    {
      path: Routes.Calendar.Path,
      name: Routes.Calendar.Name,
      component: () => import('@/pages/Calendar.vue'),
      beforeEnter: (to) => LoadCommonDataAndSetAsProps(inject(MEMBER_API)!, to),
      props: (to) => to.meta.props,
      meta: {
        authorize: true
      },
    },
    {
      path: Routes.CalendarEventDetail.Path,
      name: Routes.CalendarEventDetail.Name,
      component: () => import('@/pages/CalendarEvent.vue'),
      beforeEnter: (to) => LoadCommonDataAndSetAsProps(inject(MEMBER_API)!, to),
      props: (to) => to.meta.props,
      meta: {
        authorize: true
      },
    },
    {
      path: Routes.MyAccount.Path,
      name: Routes.MyAccount.Name,
      component: () => import('@/pages/MyAccount.vue'),
      beforeEnter: (to) => LoadCommonDataAndSetAsProps(inject(MEMBER_API)!, to),
      props: (to) => to.meta.props,
      meta: {
        authorize: true
      },
    },
    {
      path: Routes.MyAccountEdit.Path,
      name: Routes.MyAccountEdit.Name,
      component: () => import('@/pages/MyAccountEdit.vue'),
      beforeEnter: (to) => LoadCommonDataAndSetAsProps(inject(MEMBER_API)!, to),
      props: (to) => to.meta.props,
      meta: {
        authorize: true
      },
    },
    {
      path: Routes.MyAccountNotifications.Path,
      name: Routes.MyAccountNotifications.Name,
      component: () => import('@/pages/MyAccountNotifications.vue'),
      beforeEnter: (to) => LoadCommonDataAndSetAsProps(inject(MEMBER_API)!, to),
      props: (to) => to.meta.props,
      meta: {
        authorize: true
      },
    },
    {
      path: Routes.MyAccountSecurity.Path,
      name: Routes.MyAccountSecurity.Name,
      component: () => import('@/pages/MyAccountSecurity.vue'),
      beforeEnter: (to) => LoadCommonDataAndSetAsProps(inject(MEMBER_API)!, to),
      props: (to) => to.meta.props,
      meta: {
        authorize: true
      },
    },
    {
      path: Routes.StudentProfile.Path,
      name: Routes.StudentProfile.Name,
      component: () => import('@/pages/StudentProfile.vue'),
      beforeEnter: (to, from, next) => Routes.StudentProfile.BeforeRouteEnter(inject(MEMBER_API)!, to, from, next),
      props: (to) => to.meta.props,
      meta: {
        authorize: true
      },
    },
    {
      path: Routes.Files.Path,
      name: Routes.Files.Name,
      component: () => import('@/pages/FilesView.vue'),
      beforeEnter: (to) => LoadCommonDataAndSetAsProps(inject(MEMBER_API)!, to),
      props: (to) => to.meta.props,
      meta: {
        authorize: true
      },
    },
    {
      path: Routes.Groups.Path,
      name: Routes.Groups.Name,
      component: () => import('@/pages/GroupsView.vue'),
      beforeEnter: (to) => LoadCommonDataAndSetAsProps(inject(MEMBER_API)!, to),
      props: (to) => to.meta.props,
      meta: {
        authorize: true
      },
    },
    {
      path: '/:pathMatch(.*)*', // Catch all route for undefined paths
      name: Routes.NotFound.Name,
      component: () => import('@/pages/NotFound.vue'),
      meta: {}
    }
  ],
  scrollBehavior(_, __, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0, behavior: 'smooth' }
    }
  }
})

router.beforeEach(async (to, from, next) => {
  log.debug(`Navigating from '${from.path}' to '${to.path}'`);

  if (!to.redirectedFrom) {
    log.debug('This is not a redirect. Showing spinner.')
    // Redirects trigger Router.beforeEach() without triggering Router.afterEach().
    // So, we should only show the spinner if we weren't redirected, since we can't
    // know in the afterEach() method how many times beforeEach() was called.
    //useSpinnerStore().show();
  }

  const auth = inject(AUTH_STATE)!

  if ((to.meta.authorize ?? false) && !auth.isAuthenticated()) {
      log.debug('User is not authenticated. Redirecting to login page.');
      next(Routes.Login.GetHref());
  }
  else {
      next();
  }
})

router.afterEach((to) => {
  log.debug('Hiding spinner.')
  //useSpinnerStore().hide();

  nextTick(() => {
    log.debug('Setting document title.')
    document.title = `Catapult Connect - ${to.name?.toString() ?? '???'}`
    document.body.id = `${to.name?.toString() ?? '???'}`.toLowerCase().replace(/\s+/g, '-');
  })
});

export default router
